// @workflow assistant.clarification
/**
 * A clarification's whole answer (contract "Clarifications"): `{ answers: [{ questionId,
 * optionIds, text? }] }`, every question answered once, in the request's order, or
 * `{ dismissed: true }`. One normaliser reads every answer the UI meets: the user's own, the one
 * the server echoes on `client.resolved`, and the old one-question `{ optionIds, text }` that
 * stored history (and the one-question composer) still carries.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { DISMISSED_ANSWER, buildAnswer } from './clarification.js';

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

function answerFor(question, raw) {
  const answer = isPlainObject(raw) ? buildAnswer(question, raw) : null;
  return answer ? { questionId: question.id, ...answer } : null;
}

function fromAnswerList(list, questions) {
  if (list.length !== questions.length) return null;
  const byId = new Map(list.filter(isPlainObject).map((a) => [a.questionId, a]));
  if (byId.size !== list.length) return null;
  const answers = questions.map((q) => (byId.has(q.id) ? answerFor(q, byId.get(q.id)) : null));
  return answers.every(Boolean) ? { answers } : null;
}

/**
 * The answer checked against the request's questions and options.
 * @param {unknown} raw `{ answers }`, `{ dismissed: true }`, or the old `{ optionIds, text }` (one question only)
 * @param {Array<{ id: string, options: Array<{ id: string }>, multiSelect: boolean, allowFreeText: boolean }>} questions
 * @returns {{ dismissed: true } | { answers: Array<{ questionId: string, optionIds: string[], text?: string }> } | null}
 *   null when it is no answer: incomplete, a question twice or unknown, or empty
 */
export function cleanAnswer(raw, questions) {
  if (!isPlainObject(raw)) return null;
  // Dismissed needs no questions: an invalid request (never shown, no part) is answered so too.
  if (raw.dismissed === true) return DISMISSED_ANSWER;
  if (!Array.isArray(questions) || !questions.length) return null;
  if (Array.isArray(raw.answers)) return fromAnswerList(raw.answers, questions);
  if (questions.length !== 1 || (raw.optionIds === undefined && raw.text === undefined)) return null;
  const one = answerFor(questions[0], raw);
  return one ? { answers: [one] } : null;
}

/**
 * The answer to send from the user's drafts, or null while a question has none.
 * @param {Array<object>} questions
 * @param {Record<string, { optionIds?: string[], text?: string }>} drafts by question id
 */
export function buildAnswers(questions, drafts) {
  const answers = questions.map((q) => answerFor(q, (drafts && drafts[q.id]) || {}));
  return answers.length && answers.every(Boolean) ? { answers } : null;
}
